import { createContext, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import api from '../services/api'

const ProductContext = createContext({ productKey: null, productName: null, productRole: null })

/**
 * Which product the person is currently standing in, and who they are inside it.
 *
 * Somebody is one thing in ProTrack and often another in a product they work in — a Consultant
 * here who is their organisation's Admin there. While they are on that product's screens, that
 * is who they are, and the chrome should say so. Reading "Consultant" in the corner of a page
 * where you are approving your country's submissions is reading about the wrong person.
 *
 * Nothing here decides what anyone may DO. It is a label; every permission is still settled by
 * the endpoint being called, from the same tables as before.
 */
export function ProductContextProvider({ children }) {
  const { pathname } = useLocation()
  const [ctx, setCtx] = useState({ productKey: null, productName: null, productRole: null })

  // /products/<key>/… — and never ProTrack itself, where the ordinary role is the right one.
  const match = /^\/products\/([^/]+)/.exec(pathname || '')
  const key = match && match[1] !== 'protrack' ? match[1] : null

  useEffect(() => {
    if (!key) { setCtx({ productKey: null, productName: null, productRole: null }); return }
    let cancelled = false
    api.get(`/platform/products/${key}/me`)
      .then(({ data }) => {
        if (!cancelled) setCtx({ productKey: key, productName: data.name, productRole: data.my_role })
      })
      // No access, or the product is gone: say nothing rather than something wrong. The screen
      // they are heading for will explain it far better than a badge can.
      .catch(() => { if (!cancelled) setCtx({ productKey: null, productName: null, productRole: null }) })
    return () => { cancelled = true }
  }, [key])

  return <ProductContext.Provider value={ctx}>{children}</ProductContext.Provider>
}

export const useProductContext = () => useContext(ProductContext)
